<template>
  <div class="tpl-page">

    <div class="flash is-ok" v-if="flash">
      <span>{{ flash }}</span>
      <button class="flash-close" @click="flash = ''">&times;</button>
    </div>
    <div class="flash is-err" v-if="errorMessage">
      <span>{{ errorMessage }}</span>
      <button class="flash-close" @click="errorMessage = ''">&times;</button>
    </div>

    <!-- Araç çubuğu -->
    <div class="toolbar">
      <div class="search">
        <i class="fa fa-search"></i>
        <input v-model="filters.q" :placeholder="$t('templates.searchPlaceholder')" @input="onSearchInput">
        <button class="clear" v-if="filters.q" @click="filters.q = ''; load(1)">&times;</button>
      </div>

      <div class="tool-actions">
        <button class="btn-ghost" @click="openUpload()">
          <span class="icon"><i class="fa fa-cloud-upload"></i></span>
          <span>{{ $t('templates.uploadFile') }}</span>
        </button>
        <button class="btn-primary" @click="openEditor()">
          <span class="icon"><i class="fa fa-plus"></i></span>
          <span>{{ $t('templates.newTemplate') }}</span>
        </button>
      </div>
    </div>

    <div class="filters">
      <button v-for="option in statusFilters" :key="option.value"
              class="filter" :class="{ 'is-active': filters.status === option.value }"
              @click="setStatus(option.value)">
        {{ option.value ? $t('templates.' + option.labelKey) : $t('common.allStatuses') }}
      </button>

      <div class="select-wrap is-inline" v-if="categories.length">
        <select class="select-input" v-model="filters.category" @change="load(1)">
          <option value="">{{ $t('common.allCategories') }}</option>
          <option v-for="c in categories" :value="c" :key="c">{{ c }}</option>
        </select>
      </div>
    </div>

    <div class="skeleton" v-if="loading">{{ $t('templates.loadingList') }}</div>

    <div class="empty" v-else-if="!list.length">
      <span class="empty-icon"><i class="fa fa-files-o"></i></span>
      <p>{{ $t('templates.emptyTitle') }}</p>
      <button class="btn-primary" @click="openEditor()">{{ $t('templates.emptyAction') }}</button>
    </div>

    <!-- Liste -->
    <ul class="cards" v-else>
      <li class="tpl" v-for="item in list" :key="item._id">

        <span class="tpl-mark" :class="item.source === 'upload' ? 'is-file' : 'is-text'">
          <i class="fa" :class="item.source === 'upload' ? 'fa-file-pdf-o' : 'fa-pencil-square-o'"></i>
        </span>

        <div class="tpl-main" @click="fieldEditorId = item._id">
          <div class="tpl-head">
            <h3 class="tpl-name">{{ item.name }}</h3>
            <span class="pill" :class="statusPill(item.status)">{{ statusLabel(item.status) }}</span>
            <span class="ver" v-if="item.version > 1">v{{ item.version }}</span>
          </div>

          <p class="tpl-desc" v-if="item.description">{{ item.description }}</p>

          <div class="meta">
            <span v-if="item.category"><i class="fa fa-tag"></i> {{ item.category }}</span>
            <span><i class="fa fa-paper-plane-o"></i> {{ item.usageCount }}</span>
            <span><i class="fa fa-clock-o"></i> {{ $formatDate(item.updatedAt) }}</span>
          </div>
        </div>

        <div class="tpl-actions">
          <button class="icon-btn is-link" :title="$t('templates.fieldsButton')"
                  @click.stop="fieldEditorId = item._id">
            <i class="fa fa-crosshairs"></i>
          </button>
          <button class="icon-btn" :title="$t('common.edit')" @click.stop="openEditor(item)">
            <i class="fa fa-pencil"></i>
          </button>
          <button class="icon-btn" :title="$t('templates.previewFile')" @click.stop="download(item, 'preview')">
            <i class="fa fa-eye"></i>
          </button>
          <button class="icon-btn" v-if="item.source === 'upload'"
                  :title="$t('templates.originalFile')" @click.stop="download(item)">
            <i class="fa fa-download"></i>
          </button>
          <button class="icon-btn" :title="$t('common.duplicate')" @click.stop="duplicate(item)">
            <i class="fa fa-clone"></i>
          </button>
          <button class="icon-btn is-danger" :title="$t('common.delete')" @click.stop="askDelete(item)">
            <i class="fa fa-trash-o"></i>
          </button>
        </div>
      </li>
    </ul>

    <!-- Sayfalama -->
    <div class="pager" v-if="pageCount > 1">
      <button class="page-btn" :disabled="page === 1" @click="load(page - 1)">&lsaquo;</button>
      <button class="page-btn" v-for="p in pageWindow" :key="p"
              :class="{ 'is-active': p === page }" @click="load(p)">{{ p }}</button>
      <button class="page-btn" :disabled="page === pageCount" @click="load(page + 1)">&rsaquo;</button>
      <span class="pager-info">
        {{ total }} {{ $t('templates.countSuffix') }} · {{ $t('common.page') }} {{ page }} / {{ pageCount }}
      </span>
    </div>

    <!-- Şablon düzenleyici -->
    <div class="sheet" v-if="editorOpen">
      <div class="sheet-backdrop" @click="closeEditor"></div>

      <div class="sheet-card is-wide" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon"><i class="fa fa-file-text-o"></i></span>
          <div class="sheet-head-text">
            <h3>{{ form._id ? $t('templates.editTitle') : $t('templates.createTitle') }}</h3>
            <p v-if="form._id && form.usageCount > 0">
              {{ $t('templates.usedWarning', { count: form.usageCount, version: form.version + 1 }) }}
            </p>
          </div>
          <button class="sheet-close" @click="closeEditor" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body" ref="editorBody">

          <!-- Hata özeti: hangi alanlar eksik, üstte tek yerde -->
          <div class="flash is-err" v-if="errorList.length">
            <div>
              <strong>{{ errorList.length }}</strong>
              <ul class="err-list">
                <li v-for="(text, i) in errorList" :key="i">{{ text }}</li>
              </ul>
            </div>
            <button class="flash-close" @click="formErrors = {}">&times;</button>
          </div>

          <div class="field" ref="field_name" :class="{ 'has-error': formErrors.name }">
            <label>{{ $t('templates.nameLabel') }} <em>*</em></label>
            <input class="input" v-model.trim="form.name" maxlength="150"
                   :placeholder="$t('templates.namePlaceholder')" ref="input_name">
            <p class="err" v-if="formErrors.name">{{ formErrors.name }}</p>
          </div>

          <div class="form-row">
            <div class="field">
              <label>{{ $t('common.category') }}</label>
              <input class="input" v-model.trim="form.category" list="tpl-categories"
                     :placeholder="$t('templates.categoryPlaceholder')">
              <datalist id="tpl-categories">
                <option v-for="c in categories" :value="c" :key="c"></option>
              </datalist>
            </div>
            <div class="field">
              <label>{{ $t('common.status') }}</label>
              <div class="select-wrap">
                <select class="select-input" v-model="form.status">
                  <option value="draft">{{ $t('templates.statusDraft') }}</option>
                  <option value="active">{{ $t('templates.statusActive') }}</option>
                  <option value="archived">{{ $t('templates.statusArchived') }}</option>
                </select>
              </div>
            </div>
          </div>

          <p class="hint">{{ $t('templates.statusHelp') }}</p>

          <div class="field">
            <label>{{ $t('common.description') }}</label>
            <textarea class="input is-area" rows="2" v-model.trim="form.description" maxlength="1000"
                      :placeholder="$t('templates.descriptionPlaceholder')"></textarea>
          </div>

          <!-- Taraflar -->
          <div class="block" ref="field_roles" :class="{ 'has-error': formErrors.roles }">
            <div class="block-head">
              <h4>{{ $t('templates.rolesTitle') }}</h4>
              <button class="btn-ghost is-mini" @click="addRole">
                <span class="icon"><i class="fa fa-plus"></i></span>
                <span>{{ $t('templates.addRole') }}</span>
              </button>
            </div>

            <div class="role" v-for="(role, i) in form.roles" :key="'role' + i">
              <span class="role-no">{{ i + 1 }}</span>
              <input class="input" v-model.trim="role.name" :placeholder="$t('templates.rolePlaceholder')">
              <input class="input is-key" v-model.trim="role.key" :placeholder="$t('templates.roleKeyPlaceholder')">
              <div class="select-wrap is-action">
                <select class="select-input" v-model="role.action">
                  <option value="sign">{{ $t('templates.actionSign') }}</option>
                  <option value="approve">{{ $t('templates.actionApprove') }}</option>
                  <option value="fill">{{ $t('templates.actionFill') }}</option>
                  <option value="cc">{{ $t('templates.actionCc') }}</option>
                </select>
              </div>
              <button class="icon-btn is-danger" :disabled="form.roles.length === 1"
                      @click="form.roles.splice(i, 1)">
                <i class="fa fa-times"></i>
              </button>
            </div>

            <p class="err" v-if="formErrors.roles">{{ formErrors.roles }}</p>
          </div>

          <!-- İçerik -->
          <div class="field" v-if="form.source === 'blank'"
               ref="field_content" :class="{ 'has-error': formErrors.content }">
            <label>{{ $t('templates.contentLabel') }} <em>*</em></label>
            <textarea class="input is-code" rows="9" v-model="form.content.html"
                      :placeholder="$t('templates.contentPlaceholder')"></textarea>
            <p class="hint">{{ $t('templates.contentHelp', { sample: fieldSyntaxHint }) }}</p>
            <p class="err" v-if="formErrors.content">{{ formErrors.content }}</p>
          </div>

          <div class="file-note" v-else-if="form.file">
            <i class="fa fa-file-pdf-o"></i>
            <span>{{ form.file.originalName }}</span>
            <small>{{ humanSize(form.file.size) }}</small>
          </div>
        </div>

        <footer class="sheet-foot">
          <button class="btn-primary" :class="{ 'is-loading': saving }" @click="save">
            {{ $t('common.save') }}
          </button>
          <button class="btn-ghost" @click="closeEditor">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>

    <!-- Yükleme -->
    <div class="sheet" v-if="uploadOpen">
      <div class="sheet-backdrop" @click="uploadOpen = false"></div>

      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon"><i class="fa fa-cloud-upload"></i></span>
          <div class="sheet-head-text">
            <h3>{{ $t('templates.uploadTitle') }}</h3>
            <p>{{ $t('templates.uploadLimit') }}</p>
          </div>
          <button class="sheet-close" @click="uploadOpen = false" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">
          <div class="flash is-err" v-if="formErrors.file">
            <span>{{ formErrors.file }}</span>
            <button class="flash-close" @click="formErrors = {}">&times;</button>
          </div>

          <div class="drop" :class="{ 'has-file': !!uploadForm.file }" @click="$refs.fileInput.click()">
            <input class="hidden-input" type="file" accept=".pdf,.docx" ref="fileInput" @change="onFilePicked">
            <span class="drop-icon">
              <i class="fa" :class="uploadForm.file ? 'fa-file-pdf-o' : 'fa-cloud-upload'"></i>
            </span>
            <p class="drop-text" v-if="!uploadForm.file">{{ $t('templates.uploadPick') }}</p>
            <p class="drop-text" v-else><strong>{{ uploadForm.file.name }}</strong></p>
          </div>

          <div class="form-row mt">
            <div class="field">
              <label>{{ $t('templates.nameLabel') }}</label>
              <input class="input" v-model.trim="uploadForm.name"
                     :placeholder="$t('templates.uploadNamePlaceholder')">
            </div>
            <div class="field">
              <label>{{ $t('common.category') }}</label>
              <input class="input" v-model.trim="uploadForm.category">
            </div>
          </div>
        </div>

        <footer class="sheet-foot">
          <button class="btn-primary" :class="{ 'is-loading': saving }" @click="doUpload">
            {{ $t('templates.uploadFile') }}
          </button>
          <button class="btn-ghost" @click="uploadOpen = false">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>

    <!-- Silme -->
    <div class="sheet" v-if="pendingDelete">
      <div class="sheet-backdrop" @click="pendingDelete = null"></div>

      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon is-no"><i class="fa fa-trash-o"></i></span>
          <div class="sheet-head-text">
            <h3>{{ $t('templates.deleteTitle') }}</h3>
            <p>{{ $t('templates.deleteBody', { name: pendingDelete.name }) }}</p>
          </div>
          <button class="sheet-close" @click="pendingDelete = null" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body" v-if="pendingDelete.usageCount > 0">
          <p class="note is-warn">
            {{ $t('templates.deleteUsedNote', { count: pendingDelete.usageCount }) }}
          </p>
        </div>

        <footer class="sheet-foot">
          <button class="btn-danger is-solid" :class="{ 'is-loading': saving }" @click="confirmDelete">
            {{ $t('common.delete') }}
          </button>
          <button class="btn-ghost" @click="pendingDelete = null">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>

    <TemplateEditor v-if="fieldEditorId"
                    :template-id="fieldEditorId"
                    @saved="onFieldsSaved"
                    @close="fieldEditorId = null" />
  </div>
</template>

<script>
import api from '../config/api';
import TemplateEditor from './TemplateEditor.vue';

function emptyForm() {
  return {
    _id: null,
    name: '',
    description: '',
    category: '',
    status: 'draft',
    source: 'blank',
    version: 1,
    usageCount: 0,
    content: { html: '' },
    file: null,
    // Varsayılan taraf adı sunucuda da üretiliyor; burada yalnızca yeni
    // formun başlangıç değeri
    roles: [{ key: 'signer1', name: 'Signer', order: 1, action: 'sign', required: true }],
    fields: []
  };
}

// Blob'u metin olarak okur. blob.text() eski tarayıcılarda yok.
function readBlobAsText(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });
}

export default {
  name: 'Templates',
  components: { TemplateEditor },
  data() {
    return {
      list: [],
      categories: [],
      total: 0,
      page: 1,
      max: 20,
      sortBy: 'updatedAt',
      sortDir: 'desc',
      filters: { q: '', status: '', category: '' },

      loading: false,
      saving: false,
      flash: '',
      errorMessage: '',

      editorOpen: false,
      uploadOpen: false,
      pendingDelete: null,
      fieldEditorId: null,
      form: emptyForm(),
      uploadForm: { file: null, name: '', category: '' },
      formErrors: {},

      statusFilters: [
        { value: '', labelKey: '' },
        { value: 'active', labelKey: 'statusActive' },
        { value: 'draft', labelKey: 'statusDraft' },
        { value: 'archived', labelKey: 'statusArchived' }
      ],

      searchTimer: null,
      // Vue 2 derleyicisi attribute içindeki {{ }} ifadesini ve iç içe
      // süslü parantezi ayrıştıramıyor, o yüzden burada duruyorlar
      fieldSyntaxHint: '{{customer_name}}',
      columns: [
        { key: 'name', labelKey: 'templates.colName', sortable: true },
        { key: 'category', labelKey: 'common.category', sortable: true },
        { key: 'source', labelKey: 'templates.colSource', sortable: true },
        { key: 'status', labelKey: 'common.status', sortable: true },
        { key: 'usageCount', labelKey: 'templates.colUsage', sortable: true },
        { key: 'updatedAt', labelKey: 'templates.colUpdated', sortable: true }
      ]
    };
  },
  computed: {
    // Hata özeti: alanların yanındaki mesajları üstte tek listede toplar
    errorList() {
      return Object.keys(this.formErrors)
        .filter((k) => k !== 'file')
        .map((k) => this.formErrors[k]);
    },
    pageCount() {
      return Math.max(Math.ceil(this.total / this.max), 1);
    },
    // Tüm sayfa numaralarını basmak 40 sayfada ekranı dolduruyordu; pencere yeterli
    pageWindow() {
      const span = 2;
      const start = Math.max(1, Math.min(this.page - span, this.pageCount - span * 2));
      const end = Math.min(this.pageCount, start + span * 2);
      const pages = [];
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    }
  },
  mounted() {
    this.load(1);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    load(page) {
      this.page = Math.min(Math.max(page || 1, 1), this.pageCount);
      this.loading = true;
      this.errorMessage = '';

      api.get('/templates', {
        params: {
          page: this.page,
          max: this.max,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          q: this.filters.q || undefined,
          status: this.filters.status || undefined,
          category: this.filters.category || undefined
        }
      })
        .then((body) => {
          this.list = body.templateList;
          this.total = body.total;
          this.categories = body.categories;
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.loading = false; });
    },

    // Sıralama sunucuda yapılıyor: sayfa 1'deki 20 kaydı sıralamak
    // 400 kayıtlık listede yanlış sonuç veriyordu
    sort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = key;
        this.sortDir = 'asc';
      }
      this.load(1);
    },

    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.load(1), 350);
    },

    setStatus(value) {
      this.filters.status = value;
      this.load(1);
    },

    resetFilters() {
      this.filters = { q: '', status: '', category: '' };
      this.load(1);
    },

    openEditor(item) {
      this.formErrors = {};
      if (!item) {
        this.form = emptyForm();
        this.editorOpen = true;
        return;
      }
      // Liste yanıtı içeriği taşımıyor, detayı çekiyoruz
      this.loading = true;
      api.get('/templates/' + item._id)
        .then((body) => {
          const tpl = body.template;
          this.form = Object.assign(emptyForm(), tpl, {
            content: tpl.content || { html: '' },
            roles: tpl.roles && tpl.roles.length ? tpl.roles : emptyForm().roles
          });
          this.editorOpen = true;
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.loading = false; });
    },

    closeEditor() {
      this.editorOpen = false;
      this.form = emptyForm();
      this.formErrors = {};
    },

    addRole() {
      const n = this.form.roles.length + 1;
      this.form.roles.push({ key: 'signer' + n, name: '', order: n, action: 'sign', required: true });
    },

    validate() {
      const errors = {};
      if (!this.form.name || this.form.name.length < 3) {
        errors.name = this.$t('templates.nameError');
      }
      if (this.form.roles.some((r) => !r.key || !r.name)) {
        errors.roles = this.$t('templates.roleError');
      }
      const keys = this.form.roles.map((r) => r.key);
      if (new Set(keys).size !== keys.length) {
        errors.roles = this.$t('templates.roleKeyError');
      }
      if (this.form.source === 'blank' && !(this.form.content.html || '').trim()) {
        errors.content = this.$t('templates.contentError');
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },

    // Hata mesajını görüp alanı aramak zorunda kalmamak için ilk hatalı
    // alana kaydırıp odaklanıyoruz; uzun formda mesaj tek başına yetmiyor
    focusFirstError() {
      const order = ['name', 'roles', 'content'];
      const key = order.find((k) => this.formErrors[k]);
      if (!key) return;

      this.$nextTick(() => {
        const block = this.$refs['field_' + key];
        const target = Array.isArray(block) ? block[0] : block;
        if (target && target.scrollIntoView) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        const input = this.$refs['input_' + key];
        const field = Array.isArray(input) ? input[0] : input;
        if (field && field.focus) field.focus();
      });
    },

    save() {
      if (this.saving) return;                       // çift tıklama iki kayıt açıyordu
      if (!this.validate()) {
        this.focusFirstError();
        return;
      }
      this.saving = true;

      // Sunucuya tüm nesneyi değil, yalnızca düzenlenebilir alanları gönder
      const payload = {
        name: this.form.name,
        description: this.form.description,
        category: this.form.category,
        status: this.form.status,
        roles: this.form.roles,
        fields: this.form.fields,
        content: this.form.source === 'blank' ? { html: this.form.content.html } : undefined
      };

      const request = this.form._id
        ? api.put('/templates/' + this.form._id, payload)
        : api.post('/templates', payload);

      request
        .then((body) => {
          this.notify(body.message);
          this.closeEditor();
          this.load(this.page);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    openUpload() {
      this.uploadForm = { file: null, name: '', category: '' };
      this.formErrors = {};
      this.uploadOpen = true;
    },

    onFilePicked(event) {
      const file = event.target.files[0];
      this.formErrors = {};
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) {
        this.formErrors.file = this.$t('templates.uploadTooBig');
        return;
      }
      this.uploadForm.file = file;
    },

    doUpload() {
      if (this.saving) return;
      if (!this.uploadForm.file) {
        this.formErrors = { file: this.$t('templates.uploadNoFile') };
        return;
      }
      this.saving = true;

      const data = new FormData();
      data.append('file', this.uploadForm.file);
      if (this.uploadForm.name) data.append('name', this.uploadForm.name);
      if (this.uploadForm.category) data.append('category', this.uploadForm.category);

      api.post('/templates/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((body) => {
          this.notify(body.message);
          this.uploadOpen = false;
          if (this.$refs.fileInput) this.$refs.fileInput.value = '';
          this.load(1);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    duplicate(item) {
      api.post('/templates/' + item._id + '/duplicate')
        .then((body) => { this.notify(body.message); this.load(1); })
        .catch((err) => { this.errorMessage = err.message; });
    },

    // kind: 'preview' = alanları çerçeveli PDF önizleme (her şablon için),
    // aksi halde yüklenen orijinal dosya
    download(item, kind) {
      const suffix = kind === 'preview' ? '/preview' : '/file';
      // İndirme de auth header istiyor; doğrudan link açmak 401 veriyordu
      api.get('/templates/' + item._id + suffix, { responseType: 'blob' })
        .then((blob) => {
          // Sunucu hata durumunda dosya yerine JSON dönüyor, o da blob olarak geliyor
          // Sunucu 304 dönerse gövde boş gelir ve tarayıcı 0 baytlık bir
          // dosya indirir; kullanıcı da neden açılmadığını arar.
          if (!blob || blob.size === 0) {
            this.errorMessage = this.$t('common.emptyFileError');
            return;
          }
          if (blob.type && blob.type.indexOf('application/json') > -1) {
            return readBlobAsText(blob).then((text) => {
              let message = this.$t('common.downloadFailed');
              try {
                message = JSON.parse(text).message || message;
              } catch (e) {
                // gövde JSON değilse varsayılan mesaj kalsın
              }
              this.errorMessage = message;
            });
          }

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = kind === 'preview'
            ? item.name + ' ' + this.$t('templates.previewSuffix') + '.pdf'
            : (item.file ? item.file.originalName : item.name);
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => { this.errorMessage = err.message; });
    },

    onFieldsSaved(message) {
      this.notify(message);
      this.load(this.page);
    },

    askDelete(item) {
      this.pendingDelete = item;
    },

    confirmDelete() {
      if (this.saving) return;
      this.saving = true;
      api.delete('/templates/' + this.pendingDelete._id)
        .then((body) => {
          this.notify(body.message);
          this.pendingDelete = null;
          const lastOnPage = this.list.length === 1 && this.page > 1;
          this.load(lastOnPage ? this.page - 1 : this.page);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    notify(message) {
      this.flash = message;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = ''; }, 3000);
    },

    statusLabel(status) {
      const map = { draft: 'statusDraft', active: 'statusActive', archived: 'statusArchived' };
      return map[status] ? this.$t('templates.' + map[status]) : status;
    },
    statusPill(status) {
      return { draft: 'is-warn', active: 'is-ok', archived: 'is-idle' }[status] || 'is-idle';
    },
    formatDate(value) {
      return this.$formatDate(value);
    },
    humanSize(bytes) {
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB'];
      let i = 0;
      let n = bytes;
      while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
      return n.toFixed(i === 0 ? 0 : 1) + ' ' + units[i];
    }
  }
};
</script>
<style scoped>
.tpl-page { max-width: 880px; margin: 0 auto; }

/* --- Bildirim --- */
.flash {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
}

.flash.is-ok { background: #e9f7f2; color: #0f6e5c; }
.flash.is-err { background: #fdeceb; color: #a8322c; }

.flash-close {
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

.err-list { margin-top: 4px; padding-left: 16px; font-size: 13px; }
.err-list li { list-style: disc; margin-top: 2px; }

/* --- Araç çubuğu --- */
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.search {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.search i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #a7aec1;
  font-size: 13px;
}

.search input {
  width: 100%;
  height: 40px;
  border: 1px solid #e2e6f0;
  border-radius: 9px;
  padding: 0 34px 0 34px;
  font-size: 14px;
  color: #2b3146;
  background: #fff;
}

.search input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

.clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  font-size: 18px;
  color: #a7aec1;
  cursor: pointer;
}

.tool-actions { display: flex; gap: 8px; flex: 0 0 auto; }

/* --- Filtreler --- */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
}

.filter {
  background: #fff;
  border: 1px solid #e2e6f0;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
}

.filter:hover { border-color: #c3cbdd; color: #4a5175; }
.filter.is-active { background: #22223d; border-color: #22223d; color: #fff; }

.select-wrap.is-inline { margin-left: auto; min-width: 180px; }
.select-wrap.is-inline .select-input { height: 34px; font-size: 13px; }

/* --- Boş --- */
.skeleton,
.empty {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 44px 20px;
  text-align: center;
  color: #98a0b5;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 17px;
  background: #f2f4fa;
  color: #b3bacb;
  font-size: 22px;
  margin-bottom: 14px;
}

.empty p { font-size: 14.5px; color: #79809a; margin-bottom: 16px; }

/* --- Şablon kartı --- */
.cards { display: flex; flex-direction: column; gap: 10px; }

.tpl {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 15px 18px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.tpl:hover { border-color: #cfd6e6; box-shadow: 0 4px 14px rgba(24, 24, 48, 0.07); }

/* Kaynak ikonu: yüklenen mi editörde yazılan mı, bakışta belli */
.tpl-mark {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.tpl-mark.is-file { background: #fdeeec; color: #c4544c; }
.tpl-mark.is-text { background: #eef1fb; color: #5b6491; }

.tpl-main { flex: 1 1 auto; min-width: 0; cursor: pointer; }

.tpl-head { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; }

.tpl-name { font-size: 14.5px; font-weight: 700; color: #1e2233; }

.ver {
  font-size: 11px;
  font-weight: 700;
  color: #8b93a8;
  background: #f2f4fa;
  border-radius: 5px;
  padding: 1px 6px;
}

.tpl-desc {
  font-size: 12.5px;
  color: #8b93a8;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
  font-size: 12px;
  color: #98a0b5;
}

.meta i { margin-right: 4px; opacity: 0.75; }

.tpl-actions { flex: 0 0 auto; display: flex; gap: 4px; }

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #79809a;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
}

.icon-btn:hover { border-color: #c3cbdd; color: #3c4257; }
.icon-btn.is-link:hover { border-color: #00b89c; color: #0b6e5f; background: #f2fcfa; }
.icon-btn.is-danger:hover:not(:disabled) { border-color: #e0a9a5; color: #b63934; background: #fdf3f2; }
.icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* --- Sayfalama --- */
.pager { display: flex; align-items: center; gap: 5px; margin-top: 18px; }

.page-btn {
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #5a6180;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.page-btn:hover:not(:disabled) { border-color: #c3cbdd; }
.page-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.page-btn.is-active { background: #22223d; border-color: #22223d; color: #fff; }

.pager-info { margin-left: auto; font-size: 12.5px; color: #98a0b5; }

/* --- Pencere --- */
.sheet {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(20, 20, 38, 0.55);
  backdrop-filter: blur(3px);
}

.sheet-card {
  position: relative;
  width: 480px;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(16, 16, 36, 0.35);
  overflow: hidden;
}

.sheet-card.is-wide { width: 640px; }

.sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #eef0f6;
}

.sheet-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%);
  color: #06302a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.sheet-icon.is-no { background: #fdeceb; color: #a8322c; }

.sheet-head-text { flex: 1 1 auto; min-width: 0; }
.sheet-head-text h3 { font-size: 16px; font-weight: 700; color: #1e2233; }
.sheet-head-text p { font-size: 12.5px; color: #8b93a8; margin-top: 4px; line-height: 1.55; }

.sheet-close {
  background: transparent;
  border: 0;
  font-size: 24px;
  line-height: 1;
  color: #b3bacb;
  cursor: pointer;
}

.sheet-body { padding: 18px 20px 6px; overflow-y: auto; }

.sheet-foot {
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eef0f6;
  background: #fbfcfe;
}

/* --- Form --- */
.field { margin-bottom: 14px; flex: 1 1 0; min-width: 0; }

.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
  margin-bottom: 5px;
}

.field label em { color: #c4413c; font-style: normal; }

.form-row { display: flex; gap: 12px; }
.form-row.mt { margin-top: 16px; }

.input,
.select-input {
  width: 100%;
  height: 40px;
  border: 1px solid #dde1ea;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #2b3146;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input.is-area,
.input.is-code {
  height: auto;
  padding: 10px 12px;
  line-height: 1.6;
  resize: vertical;
}

.input.is-code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
}

.input:focus,
.select-input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

/* Hatalı alan kırmızı çerçeveyle işaretleniyor: mesaj tek başına
   kaybolabiliyor, alanın kendisi de görünsün */
.has-error .input,
.has-error .select-input { border-color: #e0a9a5; }
.has-error .input:focus { box-shadow: 0 0 0 3px rgba(196, 65, 60, 0.14); }

.err { font-size: 12.5px; color: #b63934; margin-top: 5px; }

.select-wrap { position: relative; display: block; }

.select-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 32px;
  cursor: pointer;
}

.select-wrap::after {
  content: "";
  position: absolute;
  right: 13px;
  top: 50%;
  margin-top: -2px;
  width: 0;
  height: 0;
  border-left: 4.5px solid transparent;
  border-right: 4.5px solid transparent;
  border-top: 5px solid #8b93a8;
  pointer-events: none;
}

.hint { font-size: 12.5px; color: #98a0b5; line-height: 1.6; margin-bottom: 14px; }

.note { border-radius: 10px; padding: 12px 14px; font-size: 12.5px; line-height: 1.6; }
.note.is-warn { background: #fdf5e6; color: #7d5713; }

/* --- Taraflar --- */
.block {
  border: 1px solid #eef0f6;
  border-radius: 11px;
  padding: 14px;
  margin-bottom: 16px;
}

.block.has-error { border-color: #f0cdcb; background: #fefaf9; }

.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 11px;
}

.block-head h4 { font-size: 13px; font-weight: 700; color: #3c4257; }

.role { display: flex; align-items: center; gap: 7px; margin-bottom: 7px; }

.role-no {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: #f1f3fa;
  color: #79809a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-weight: 700;
}

.role .input { height: 36px; font-size: 13px; }
.role .input.is-key { flex: 0 0 110px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; }
.role .select-wrap.is-action { flex: 0 0 130px; }
.role .select-input { height: 36px; font-size: 13px; }
.role .icon-btn { width: 30px; height: 30px; }

.file-note {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f7f9fd;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13.5px;
  color: #3c4257;
}

.file-note i { color: #c4544c; font-size: 16px; }
.file-note small { color: #98a0b5; margin-left: auto; }

/* --- Dosya bırakma --- */
.drop {
  border: 2px dashed #d5dae6;
  border-radius: 12px;
  padding: 28px 16px;
  text-align: center;
  cursor: pointer;
  background: #fbfcfe;
  transition: border-color 0.15s, background-color 0.15s;
}

.drop:hover { border-color: #00b89c; background: #f2fcfa; }
.drop.has-file { border-style: solid; border-color: #bfe8df; background: #f4fdfa; }

.hidden-input { display: none; }

.drop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: #eef1f8;
  color: #8b93a8;
  font-size: 18px;
  margin-bottom: 9px;
}

.drop.has-file .drop-icon { background: #e0f6f0; color: #0f8d78; }
.drop-text { font-size: 13.5px; color: #5a6180; }

/* --- Rozet --- */
.pill {
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.pill.is-ok { background: #e4f6ef; color: #0f6e5c; }
.pill.is-warn { background: #fdf2e2; color: #a66a12; }
.pill.is-idle { background: #f2f4fa; color: #7a819a; }

/* --- Düğmeler --- */
.btn-primary,
.btn-ghost,
.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 9px;
  padding: 10px 17px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 3px 10px rgba(0, 168, 143, 0.28);
}

.btn-primary:hover { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }
.btn-primary.is-loading { opacity: 0.7; pointer-events: none; }

.btn-ghost { background: #fff; border: 1px solid #dde1ea; color: #3c4257; }
.btn-ghost:hover { border-color: #b9c0d2; }
.btn-ghost.is-mini { padding: 6px 11px; font-size: 12.5px; }

.btn-danger { background: #fff; border: 1px solid #f0cdcb; color: #b63934; }
.btn-danger.is-solid { background: #c4413c; border-color: #c4413c; color: #fff; }
.btn-danger.is-solid:hover { background: #ad3833; }
.btn-danger.is-loading { opacity: 0.7; pointer-events: none; }

@media screen and (max-width: 720px) {
  .toolbar { flex-direction: column; align-items: stretch; }
  .tool-actions { justify-content: stretch; }
  .tool-actions button { flex: 1 1 0; justify-content: center; }
  .select-wrap.is-inline { margin-left: 0; width: 100%; }
  .tpl { flex-wrap: wrap; }
  .tpl-actions { width: 100%; justify-content: flex-end; }
  .form-row { flex-direction: column; gap: 0; }
  .role { flex-wrap: wrap; }
  .role .input.is-key, .role .select-wrap.is-action { flex: 1 1 100%; }
  .sheet { padding: 0; align-items: flex-end; }
  .sheet-card { width: 100%; border-radius: 16px 16px 0 0; max-height: 92vh; }
}
</style>


// WEBPACK FOOTER //
// src/Components/Templates.vue